import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalContent, Button } from '@heroui/react';
import { usePlanApprovalStore } from '@stores/planApprovalStore';
import { usePlatformTrial } from '@hooks/usePlatformTrial';
import { useAuthStore } from '@stores/authStore';
import { PLANS_PATH } from '@lib/services/credit/platformTrial';
import { startShopifyPlanApproval } from '@lib/services/payment/planApproval';
import { hasShopifyInstallation } from '@lib/services/shopify/shopifyApi';
import { PLAN_TIERS } from './planTiers';

const ACCENT = '#6C5CE7';

// Name, price and tagline must read from PLAN_TIERS, never be hard-coded.
const GROWTH = PLAN_TIERS.find(tier => tier.key === 'growth')!;

/**
 * Day-15 platform-trial approval dialog (PRD V6.0 ①/③) — the single instance
 * in the app, owned by `PlatformTrialController` and opened from anywhere
 * (the read-only bar, `CreditExhaustionModal` once `awaitingApproval`) via
 * `usePlanApprovalStore`.
 *
 * "Approve in Shopify" reuses the exact path a merchant choosing Growth from
 * the in-app plans page already goes through (`startShopifyPlanApproval`) —
 * picking Growth again during the trial IS the approval (backend clears
 * `requiresApproval` on `activate()`).
 */
export function ApprovePlanModal() {
  const isOpen = usePlanApprovalStore(s => s.isOpen);
  const close = usePlanApprovalStore(s => s.close);
  const { daysLeft } = usePlatformTrial();
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A reopen must not carry over a previous attempt's error/loading state.
  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (submitting) return;
    close();
  };

  const goToPlans = () => {
    close();
    navigate(PLANS_PATH);
  };

  const handleApprove = async () => {
    if (!user) return;
    if (!hasShopifyInstallation(user)) {
      goToPlans();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const url = await startShopifyPlanApproval(user, GROWTH.realPlanId!, false);
      window.location.href = url;
    } catch (e) {
      // Log the failure, never the (not-yet-obtained) confirmation URL.
      console.error('[ApprovePlanModal] startShopifyPlanApproval failed', e);
      setError("We couldn't open Shopify. Please try again.");
      setSubmitting(false);
    }
  };

  const lead =
    daysLeft != null && daysLeft > 0
      ? "You've used all your free trial credits."
      : 'Your free trial has ended.';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" placement="center" hideCloseButton>
      <ModalContent className="rounded-[28px]">
        <div className="px-8 py-8">
          <h2 className="text-xl font-extrabold leading-snug text-gray-900">
            Approve your plan to continue
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            {lead} Approve the Growth plan in Shopify to keep your AI team working.
          </p>

          <div className="mt-4 rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="font-bold text-gray-900">{GROWTH.name}</span>
              <span className="font-bold text-gray-900">${GROWTH.monthly} / month</span>
            </div>
            <p className="mt-1 text-sm text-gray-500">{GROWTH.tagline}</p>
          </div>

          {error && (
            <p role="alert" className="mt-3 text-sm text-red-600">
              {error}
            </p>
          )}

          <Button
            className="mt-4 w-full rounded-xl py-6 text-sm font-bold text-white"
            style={{ backgroundColor: ACCENT }}
            isLoading={submitting}
            isDisabled={submitting}
            onPress={handleApprove}
          >
            Approve in Shopify
          </Button>
          <Button
            variant="light"
            className="mt-2 w-full"
            isDisabled={submitting}
            onPress={goToPlans}
          >
            Choose a different plan
          </Button>
          <Button
            variant="light"
            className="mt-1 w-full"
            isDisabled={submitting}
            onPress={handleClose}
          >
            Not now
          </Button>

          <p className="mt-4 text-center text-xs text-gray-400">
            Your conversations and saved work are still available to read.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
}
